/**
 * Copyright © PlanMaster Fogászati Szoftver Kft. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Tamas Neufeld <tamas.neufeld@planmaster3d.com>, 2018
 */

export function getToothRanges(ids, asText) {
    try {
        if (!Array.isArray(ids)) ids = String(ids).split(",");
        const sorted = Helper.sortToothNumbers(ids, "round");
        const groups = [[]];
        let selectionIndex = 0;
        let prevId = null;

        for (const tIndex in sorted) {
            const t = sorted[tIndex];
            if (prevId===null || Helper.isAdjacents(prevId, t)) {
                groups[selectionIndex].push(t);
            } else {
                selectionIndex++;
                groups.push([t]);
            }
            prevId = t;
        }

        if (asText) {
            const final = [];
            for (const i in groups) {
                if (groups[i].length > 2) {
                    final.push(Helper.getToothNotation(groups[i][0])+"-"+Helper.getToothNotation(groups[i][groups[i].length-1]));
                } else {
                    groups[i] = Helper.sortToothNumbers(groups[i], "frontToBack");
                    if (groups[i].length === 2) final.push(Helper.getToothNotation(groups[i][1]));
                    final.push(Helper.getToothNotation(groups[i][0]));
                }
            }
            // Debug.info('Tooth ranges generated', final);
            return final.join(", ");
        } else {
            // Debug.info('Tooth ranges generated', groups);
            return groups;
        }
    } catch(error) {
        captureException(error);
        console.error("Error getting tooth ranges", typeof(ids), ids, error);
        return "-";
    }
}

export function getToothRange(toothIds) {
    if (typeof(toothIds)==="string") var ids = toothIds.split(",");
    else var ids = toothIds;
    if (typeof(ids)==="undefined") {
        return "";
    }

    if (ids.length === 1) return ids[0];

    const sorted = this.sortToothNumbers(ids, "round");
    if (ids.length>1) {
        const l = this.getTeethCountBetween(sorted);
        if (l === sorted.length ) {
            return sorted[0] < sorted[sorted.length-1] ? sorted[0]+"-"+sorted[sorted.length-1] : sorted[sorted.length-1]+"-"+sorted[0];
        } else return this.sortToothNumbers(ids, "frontToBack").join(", ");
    }
    return this.sortToothNumbers(ids, "frontToBack").join(", ");
}

/**
 * Helper to sort tooth numbers: 18->11,21->28,48->41,31->38
 * @return array
 */
export function sortToothNumbers(selection, method) {
    if (typeof (selection)==="undefined") return null;
    if (selection[0] == 0) return selection;

    const regions = {r1: [], r2: [], r3: [], r4: []};
    for (const i in selection) {
        const r = "r"+Math.floor(selection[i]/10);
        if (!regions.hasOwnProperty(r)) continue;
        regions[r].push(selection[i]);
    }

    switch (method) {
        case "frontToBack":
            regions.r1.sort();
            regions.r2.sort();
            regions.r3.sort();
            regions.r4.sort();
            break;
        case "round":
        default:
            regions.r1.sort().reverse();
            regions.r2.sort();
            regions.r3.sort().reverse();
            regions.r4.sort();
            break;
    }

    selection = regions.r1.concat(regions.r2, regions.r3, regions.r4);

    const uniqueSelection = selection.filter(function(item, pos) {
        return selection.indexOf(item) == pos;
    });

    return uniqueSelection;
}

export function isAdjacents(t1, t2) {
    t1 = parseInt(t1);
    t2 = parseInt(t2);
    if (t1<30 && t2>30 || t1>30 && t2<30) return false;
    return (Math.abs(parseInt(t1)-parseInt(t2))===1 || (t1 % 10 === 1 && t2 % 10 === 1));
}

export function getJaw(selection) {
    const splitted = Helper.splitJaws(selection);
    if (splitted.lower.length > 0) return "lower";
    else return "upper";
}

export function splitJaws(selection) {
    if (selection === null) return ({upper: [], lower: []});
    if (typeof(selection)==="string" || typeof(selection)==="number") selection = [parseInt(selection)];
    const upper = [];
    const lower = [];
    for (const i in selection) {
        const t = parseInt(selection[i]);
        if (t<30) upper.push(t);
        else lower.push(t);
    }
    return ({
        upper: upper,
        lower: lower,
    });
}

/**
 * Helper to detect if selected teeth are adjacent
 * @return int, the number of teeth between the 2 furthest teeth from the selection
 */
export function getTeethCountBetween(selection) {
    if (typeof (selection)==="undefined") return null;
    if (selection.length===1) return 1;
    selection = this.sortToothNumbers(selection);

    const firstTooth = selection[0];
    const lastTooth = selection[selection.length-1];
    const regionFirst = Math.floor(firstTooth/10);
    const numFirst = firstTooth%10;
    const regionLast = Math.floor(lastTooth/10);
    const numLast = lastTooth%10;
    if (regionFirst === regionLast) return ((Math.abs(numFirst - numLast))+1);
    else return (numFirst+numLast);
}

export function getTeethIdsBetween(selection) {
    if (typeof (selection)==="undefined") return null;
    if (selection.length===1) return selection;
    selection = this.sortToothNumbers(selection);

    const tids = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28, 38, 37, 36, 35, 34, 33, 32, 31, 41, 42, 43, 44, 45, 46, 47, 48];
    const firstIdx = tids.indexOf(selection[0]);
    const lastIdx = tids.indexOf(selection[selection.length-1]);
    return tids.slice(firstIdx, lastIdx + 1);
}

export function isToothIdInRange(tId, range) {
    const ids = this.getTeethIdsBetween(range);
    return ids.includes(parseInt(tId));
}

/**
 *
 * @param toothId
 * @param notation
 * https://en.wikipedia.org/wiki/Dental_notation
 */
export function getToothNotation(toothId, notation = null) {
    if (!notation) notation = app.$store.state.dentalNotation;

    if (typeof toothId === "object") {
        const ret = [];
        toothId.forEach((id) => {
            ret.push(this.getToothNotation(id, notation));
        });
        return ret;
    }

    toothId = parseInt(toothId);
    if (isNaN(toothId) || toothId === 0) return 0;

    // Palmer notation, see: https://en.wikipedia.org/wiki/Palmer_notation
    if (notation === "Palmer") {
        const region = Math.floor(toothId / 10);
        const id = toothId % 10;
        if (region === 1) return id + "┘";
        if (region === 2) return "└" + id;
        if (region === 3) return "┌" + id;
        if (region === 4) return id + "┐";
    }

    // USA, see: https://en.wikipedia.org/wiki/Universal_numbering_system_(dental)
    if (notation === "USA") {
        return [0, 18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28, 38, 37, 36, 35, 34, 33, 32, 31, 41, 42, 43, 44, 45, 46, 47, 48].indexOf(toothId);
    }

    // default ISO/FDI notation, see: https://en.wikipedia.org/wiki/Universal_numbering_system_(dental)
    else return toothId;
}

export function isStepInRegion(step, region) {
    region = parseInt(region);
    const ids = step.tooth_id.split(",");
    const regionIds = Helper.getRegionTeethIds(region);
    for (const i in ids) {
        if (regionIds.includes(parseInt(ids[i]))) return true;
    }
    return false;
}

export function isTeethInRegion(toothIds, region) {
    region = parseInt(region);
    const regionIds = Helper.getRegionTeethIds(region);
    for (const i in toothIds) {
        if (regionIds.includes(parseInt(toothIds[i]))) return true;
    }
    return false;
}

export function getRegionTeethIds(region) {
    region = parseInt(region);
    if (region === 0) return [17, 16, 15, 14];
    if (region === 1) return [24, 25, 26, 27];
    if (region === 2) return [13, 12, 11, 21, 22, 23];
    if (region === 3) return [47, 46, 45, 44];
    if (region === 4) return [34, 35, 36, 37];
    if (region === 5) return [43, 42, 41, 31, 32, 33];
    return [];
}

export function updateStepToothIds(step, selection) {
    if (selection.length > 1) {
        selection = selection.filter((el)=>{
            return parseInt(el) !== 0 && !isNaN(parseInt(el));
        });
    }
    step.tooth_id = selection.join(",");
    if (step.tooth_id.charAt(0) === ",") step.tooth_id = step.tooth_id.substr(1);
    step.amount = parseInt(step.batch_price) === 1 ? 1 : selection.length;
    step.price = step.amount * parseFloat(step.unit_price);
    if (typeof step.options !== "undefined") {
        step.options.forEach((option) => {
            option.tooth_id = step.tooth_id;
            option.amount = step.amount;
            option.price = option.amount * option.unit_price;
        });
    }
}

export function breakBridgesToParts(toothIds) {
    let parts = [];
    let subSelection = [];
    let prevId = null;

    toothIds.forEach((tId) => {
        if (prevId !== null) {
            if (!Helper.isAdjacents(prevId, tId)) {
                parts.push(JSON.parse(JSON.stringify(subSelection)));
                subSelection = [];
            }
        }
        subSelection.push(tId);
        prevId = tId;
    });

    if (subSelection.length > 0) parts.push(JSON.parse(JSON.stringify(subSelection)));

    return parts;
}

export function getUnpillaredBridgeParts(bridgeIds, implantIds) {
    const implantRange = Helper.getTeethIdsBetween([implantIds[0], implantIds[implantIds.length-1]]);
    const parts = [];
    const unpillared = [];
    let partId = 0;
    let isPrevIncluded = false;
    bridgeIds.forEach((tId) => {
        if (!implantRange.includes(parseInt(tId))) {
            unpillared.push(tId);
        }
    });
    return Helper.breakBridgesToParts(unpillared);
}

export function getPrevRegion(region) {
            region = parseInt(region);
            if (region === 1) return 2;
            if (region === 2) return 0;
            if (region === 4) return 5;
            if (region === 5) return 3;
            return null;
        }

       export function getNextRegion(region) {
            region = parseInt(region);
            if (region === 0) return 2;
            if (region === 2) return 1;
            if (region === 3) return 5;
            if (region === 5) return 4;
            return null;
        }
